import React, { useState, useEffect } from 'react';
import { accountsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { fmtMoney } from '../../utils/currency';

const fmt = fmtMoney;

export default function AccountList() {
  const { hasPermission } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const [a, t] = await Promise.all([accountsAPI.list({ active: 'true' }), accountsAPI.types()]);
      setAccounts(a);
      setTypes(t);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (form.id) {
        await accountsAPI.update(form.id, form);
        toast.success('Account updated');
      } else {
        await accountsAPI.create(form);
        toast.success('Account created');
      }
      setShowForm(false);
      setForm({});
      load();
    } catch (err) { toast.error(err.message); }
  };

  const filtered = accounts.filter(a => {
    if (filter && a.account_type_id !== parseInt(filter)) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase()) && !a.code.includes(search)) return false;
    return true;
  });

  // Group by type
  const grouped = {};
  filtered.forEach(a => {
    const type = a.type_name || 'Other';
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(a);
  });

  return (
    <div>
      <div className="toolbar">
        <div className="toolbar-left">
          <h2>Chart of Accounts</h2>
        </div>
        <div className="toolbar-right">
          {hasPermission('accounts:create') && (
            <button className="btn btn-primary" onClick={() => { setForm({}); setShowForm(true); }}>+ New Account</button>
          )}
        </div>
      </div>

      <div className="filters-bar">
        <input className="form-control" placeholder="Search accounts..." value={search} onChange={e => setSearch(e.target.value)} style={{width:250}} />
        <select className="form-control" value={filter} onChange={e => setFilter(e.target.value)} style={{width:200}}>
          <option value="">All Types</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {loading ? <div className="spinner" style={{margin:'40px auto'}} /> : (
        <div className="card">
          <div className="card-body" style={{padding:0}}>
            <table>
              <thead>
                <tr><th>Code</th><th>Account Name</th><th>Type</th><th>Balance</th><th>Status</th></tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([type, accts]) => (
                  <React.Fragment key={type}>
                    <tr style={{background:'#f8fafc'}}><td colSpan={5} style={{fontWeight:600,padding:'10px 16px',fontSize:13,color:'#475569'}}>{type}</td></tr>
                    {accts.map(a => (
                      <tr key={a.id} onClick={() => { setForm(a); setShowForm(true); }} style={{cursor:'pointer'}}>
                        <td className="font-mono">{a.code}</td>
                        <td>{a.name}</td>
                        <td><span className="badge badge-draft">{a.category}</span></td>
                        <td className="amount">{fmt(a.balance || a.opening_balance)}</td>
                        <td>{a.is_active ? '✅' : '❌'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {filtered.length === 0 && <tr><td colSpan={5} className="text-center text-muted" style={{padding:30}}>No accounts found</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{form.id ? 'Edit Account' : 'New Account'}</h3>
              <button className="btn-ghost" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Account Type <span className="required">*</span></label>
                    <select className="form-control" required value={form.account_type_id || ''} onChange={e => setForm({...form, account_type_id: e.target.value})}>
                      <option value="">Select type...</option>
                      {types.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Code <span className="required">*</span></label>
                    <input className="form-control" required value={form.code || ''} onChange={e => setForm({...form, code: e.target.value})} placeholder="e.g. 1000" />
                  </div>
                  <div className="form-group full-width">
                    <label>Name <span className="required">*</span></label>
                    <input className="form-control" required value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} placeholder="Account name" />
                  </div>
                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea className="form-control" value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>Opening Balance</label>
                    <input className="form-control" type="number" step="0.01" value={form.opening_balance || ''} onChange={e => setForm({...form, opening_balance: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>Opening Balance Date</label>
                    <input className="form-control" type="date" value={form.opening_balance_date || ''} onChange={e => setForm({...form, opening_balance_date: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label><input type="checkbox" checked={form.is_bank_account || false} onChange={e => setForm({...form, is_bank_account: e.target.checked})} /> Bank Account</label>
                  </div>
                  <div className="form-group">
                    <label><input type="checkbox" checked={form.is_cash_account || false} onChange={e => setForm({...form, is_cash_account: e.target.checked})} /> Cash Account</label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{form.id ? 'Update' : 'Create'} Account</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
